import { useState, useEffect } from "react";
import { Search, Download, X, Plus } from "lucide-react";
import axios from "axios";
import NavBar from "@/components/NavBar";
import { useToast } from "@/hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SelectField = ({ label, value, onChange, options, placeholder, testId }) => (
  <div>
    <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
      {label}
    </label>
    <div className="relative">
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-4 pr-10 text-neutral-50 focus:border-amber-400 focus:outline-none appearance-none text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  </div>
);

const InventarioGeneralPage = () => {
  const { toast } = useToast();
  const [sedes, setSedes] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSede, setSelectedSede] = useState("");
  const [selectedFecha, setSelectedFecha] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [responsable, setResponsable] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [newProductName, setNewProductName] = useState("");
  const [quantities, setQuantities] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [isSavingProduct, setIsSavingProduct] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sedesRes, productsRes] = await Promise.all([
          axios.get(`${API}/sedes`, { params: { skip: 0, limit: 1000 } }),
          axios.get(`${API}/productos/inventario-general`, { params: { skip: 0, limit: 1000 } }),
        ]);
        const sedesList = sedesRes.data.items ?? sedesRes.data;
        const productsList = productsRes.data.items ?? productsRes.data;
        setSedes(sedesList);
        setProducts(productsList);
        if (sedesList.length > 0) setSelectedSede(sedesList[0].id);
      } catch (err) {
        console.error("Error cargando datos:", err);
        toast({
          title: "Error al cargar datos",
          description: err.response?.status === 500
            ? "Error del servidor. Intenta más tarde."
            : "No se pudieron cargar los datos. Verifica tu conexión.",
          variant: "destructive",
        });
      }
    };
    fetchData();
  }, [toast]);

  const updateQuantity = (productId, value) => {
    const normalizedValue = value === "" ? "" : value.replace(/[^0-9]/g, "");
    setQuantities((prev) => ({
      ...prev,
      [productId]: normalizedValue === "" ? "" : Number(normalizedValue),
    }));
  };

  const addProduct = async () => {
    if (!newProductName.trim()) return;
    setIsSavingProduct(true);
    try {
      const res = await axios.post(`${API}/productos/inventario-general`, {
        name: newProductName.trim(),
        unit: null,
        codigo: null,
      });
      const created = res.data;
      setProducts((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewProductName("");
      toast({
        title: "Producto agregado",
        description: "Se agregó el nuevo producto en Inventario General.",
      });
    } catch (err) {
      console.error("Error agregando producto:", err);
      toast({
        title: "Error al agregar",
        description: "No se pudo crear el producto. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSavingProduct(false);
    }
  };

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sedeName = sedes.find((s) => s.id === selectedSede)?.name || "—";
  const formattedDate = selectedFecha
    ? new Date(selectedFecha + "T12:00:00").toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "—";

  const exportExcel = async () => {
    setIsExporting(true);
    try {
      const ExcelJS = (await import("exceljs")).default;
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Sistema de Inventario";
      workbook.created = new Date();

      const ws = workbook.addWorksheet("Inventario General");

      const infoStyle = { font: { size: 11 }, alignment: { vertical: "middle" } };
      const infoLabelStyle = { font: { bold: true, size: 11 }, alignment: { vertical: "middle" } };

      ws.getRow(1).getCell(1).value = "SEDE:";
      ws.getRow(1).getCell(2).value = sedeName;
      ws.getRow(2).getCell(1).value = "FECHA:";
      ws.getRow(2).getCell(2).value = formattedDate;
      ws.getRow(3).getCell(1).value = "RESPONSABLE:";
      ws.getRow(3).getCell(2).value = responsable || "—";

      [1, 2, 3].forEach((rowNum) => {
        ws.getRow(rowNum).getCell(1).style = infoLabelStyle;
        ws.getRow(rowNum).getCell(2).style = infoStyle;
        ws.getRow(rowNum).height = 18;
      });

      ws.getRow(4).height = 6;

      ws.getRow(5).values = ["CÓDIGO", "PRODUCTO", "CANTIDAD ACTUAL", "ESTADO"];
      const headerRow = ws.getRow(5);
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF16A34A" },
        };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin", color: { argb: "FF14532D" } },
          bottom: { style: "thin", color: { argb: "FF14532D" } },
          left: { style: "thin", color: { argb: "FF14532D" } },
          right: { style: "thin", color: { argb: "FF14532D" } },
        };
      });

      ws.getColumn(1).width = 16;
      ws.getColumn(2).width = 42;
      ws.getColumn(3).width = 20;
      ws.getColumn(4).width = 14;

      products.forEach((p, index) => {
        const rawQty = quantities[p.id];
        const cantidad = rawQty !== "" && rawQty !== undefined ? Number(rawQty) : 0;
        const estado = cantidad > 0 ? "OK" : "AGOTADO";
        const dataRow = ws.getRow(6 + index);
        dataRow.values = [p.codigo || "—", p.name, cantidad, estado];
        dataRow.height = 20;

        const isEven = index % 2 === 0;
        [1, 2, 3, 4].forEach((colNum) => {
          const cell = dataRow.getCell(colNum);
          if (isEven) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF9FAFB" },
            };
          }
          cell.border = {
            top: { style: "hair", color: { argb: "FFE5E7EB" } },
            bottom: { style: "hair", color: { argb: "FFE5E7EB" } },
            left: { style: "hair", color: { argb: "FFE5E7EB" } },
            right: { style: "hair", color: { argb: "FFE5E7EB" } },
          };
        });

        dataRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
        dataRow.getCell(1).font = { name: "Courier New", color: { argb: "FF9CA3AF" }, size: 10 };

        dataRow.getCell(3).alignment = { horizontal: "center", vertical: "middle" };
        dataRow.getCell(3).font = {
          name: "Courier New",
          size: 11,
          bold: cantidad > 0,
        };

        const estadoCell = dataRow.getCell(4);
        estadoCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: cantidad > 0 ? "FFD1FAE5" : "FFFEE2E2" },
        };
        estadoCell.font = {
          color: { argb: cantidad > 0 ? "FF065F46" : "FF7F1D1D" },
          bold: true,
          size: 10,
        };
        estadoCell.alignment = { horizontal: "center", vertical: "middle" };
      });

      ws.views = [{ state: "frozen", ySplit: 5 }];

      if (products.length > 0) {
        ws.autoFilter = {
          from: { row: 5, column: 1 },
          to: { row: 5 + products.length, column: 4 },
        };
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = `inventario_general_${selectedFecha || "fecha"}_${sedeName}.xlsx`.replace(/\s+/g, "_");
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generando Excel:", err);
      toast({
        title: "Error al exportar",
        description: "No se pudo generar el archivo Excel. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      className="bg-neutral-950 flex flex-col max-w-md mx-auto relative"
      style={{ fontFamily: "'Outfit', sans-serif", minHeight: "100dvh" }}
    >
      <NavBar title="Inventario General" subtitle="Control general de stock" />

      <div className="px-4 pt-4 pb-3 space-y-3 border-b border-neutral-800 bg-neutral-950">
        <SelectField
          label="Sede"
          value={selectedSede}
          onChange={setSelectedSede}
          options={sedes}
          placeholder="— Seleccionar sede —"
          testId="inv-general-select-sede"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
              Fecha
            </label>
            <input
              data-testid="inv-general-input-fecha"
              type="date"
              value={selectedFecha}
              onChange={(e) => setSelectedFecha(e.target.value)}
              className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-3 text-neutral-50 focus:border-amber-400 focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
              Responsable
            </label>
            <input
              data-testid="inv-general-input-responsable"
              type="text"
              placeholder="Nombre del responsable"
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-3 text-neutral-50 placeholder:text-neutral-600 focus:border-amber-400 focus:outline-none text-sm"
            />
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-neutral-800 bg-neutral-950 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            data-testid="inv-general-input-search"
            type="text"
            placeholder="Buscar producto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 bg-neutral-900 border border-neutral-800 rounded-md pl-9 pr-9 text-neutral-50 placeholder:text-neutral-600 focus:border-amber-400 focus:outline-none text-sm"
          />
          {searchQuery && (
            <button
              data-testid="inv-general-btn-clear-search"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-neutral-500" />
            </button>
          )}
        </div>

        <div className="flex gap-2">
          <input
            data-testid="inv-general-input-add-product"
            type="text"
            placeholder="Agregar nuevo producto"
            value={newProductName}
            onChange={(e) => setNewProductName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addProduct()}
            className="flex-1 h-11 bg-neutral-900 border border-neutral-800 rounded-md px-3 text-neutral-50 placeholder:text-neutral-600 focus:border-amber-400 focus:outline-none text-sm"
          />
          <button
            data-testid="inv-general-btn-add-product"
            onClick={addProduct}
            disabled={isSavingProduct || !newProductName.trim()}
            className="h-11 px-3 bg-amber-400 text-neutral-950 rounded-md disabled:opacity-50 flex items-center justify-center"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="px-4 py-2 flex items-center bg-neutral-950 border-b border-neutral-800">
        <div className="flex-1 text-xs font-semibold tracking-widest uppercase text-neutral-500">
          Producto
        </div>
        <div className="w-24 text-center text-xs font-semibold tracking-widest uppercase text-neutral-500">
          Cantidad actual
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-28">
        {products.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Sin productos configurados.</p>
            <p className="text-neutral-600 text-xs mt-1">
              Agrega el primero desde este apartado o en Administración.
            </p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="px-4 py-10 text-center text-neutral-500 text-sm">
            No se encontraron productos con ese nombre.
          </div>
        ) : (
          filteredProducts.map((product) => {
            const qty = quantities[product.id];
            const hasQty = qty !== undefined && qty !== "" && Number(qty) > 0;
            return (
              <div
                key={product.id}
                data-testid={`inv-general-product-row-${product.id}`}
                className={`flex items-center px-4 py-3 border-b border-neutral-800 transition-colors duration-150 ${
                  hasQty ? "bg-amber-950/20" : ""
                }`}
              >
                <div className="flex-1 min-w-0 pr-3">
                  <div
                    className={`text-sm font-medium truncate ${
                      hasQty ? "text-amber-400" : "text-neutral-50"
                    }`}
                  >
                    {product.name}
                  </div>
                  <div className="text-xs text-neutral-500 font-mono-data">
                    {product.unit || "—"}
                  </div>
                </div>
                <input
                  data-testid={`inv-general-input-cantidad-${product.id}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  enterKeyHint="next"
                  value={quantities[product.id] ?? ""}
                  onChange={(e) => updateQuantity(product.id, e.target.value)}
                  placeholder="0"
                  className={`w-24 h-11 border rounded text-center text-sm font-mono-data focus:outline-none transition-colors duration-150 ${
                    hasQty
                      ? "bg-amber-950 border-amber-400/60 text-amber-400 focus:border-amber-400"
                      : "bg-neutral-800 border-neutral-700 text-neutral-50 focus:border-neutral-600"
                  }`}
                />
              </div>
            );
          })
        )}
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-3 bg-neutral-950/95 backdrop-blur border-t border-neutral-800 max-w-md mx-auto w-full z-40"
        style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
      >
        <button
          data-testid="export-inv-general-excel-button"
          onClick={exportExcel}
          disabled={products.length === 0 || isExporting}
          className={`w-full h-14 rounded-md font-semibold text-base flex items-center justify-center gap-2 transition-colors duration-150 ${
            products.length > 0 && !isExporting
              ? "bg-amber-400 text-neutral-950 active:bg-amber-300"
              : "bg-neutral-800 text-neutral-600 cursor-not-allowed"
          }`}
        >
          <Download className="w-5 h-5" />
          {isExporting
            ? "Generando Excel..."
            : products.length > 0
            ? `Exportar a Excel (${products.length} producto${products.length !== 1 ? "s" : ""})`
            : "Sin productos"}
        </button>
      </div>
    </div>
  );
};

export default InventarioGeneralPage;
